// [YOUR_FILE_NAME].jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import {
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  DEFAULT_PDF_COLORS,
  usePdfSettings,
} from "../../../layouts/PdfSettingsContext.js";
import { useBranch } from "../../../layouts/BranchContext.js";

/* =========================================================
   API
========================================================= */

const API = axios.create({
  baseURL: "https://wims.revisetoexcel.com/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   AUTH
========================================================= */

API.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   ENDPOINTS
========================================================= */

const REPORT_ENDPOINTS = {
  "book-issue": "/library/reports/book-issue/",
  "book-reservation":
    "/library/reports/book-reservation/",
  "book-stock": "/library/reports/book-stock/",
  "fine-collection":
    "/library/reports/fine-collection/",
  "member-activity":
    "/library/reports/member-activity/",
};

const ORGANIZATION_URL =
  "/master/organizations/";

const MEMBER_DROPDOWN_URL =
  "/library/library-member/dropdown/";

const BOOK_DROPDOWN_URL =
  "/library/book-master/dropdown/";

const BOOK_MASTER_LIST_URL =
  "/library/book-master/list/";

/* =========================================================
   HELPERS
========================================================= */

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  return [];
};

const getStoredUser = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem("user") ||
        sessionStorage.getItem("userData") ||
        sessionStorage.getItem("currentUser") ||
        "{}"
    );
  } catch {
    return {};
  }
};

const getId = (item) =>
  item?.id ??
  item?.value ??
  item?.organization_id ??
  item?.branch_id ??
  item?.library_member_id ??
  item?.book_id;

const getOrganizationName = (item) =>
  item?.name ||
  item?.organization_name ||
  item?.organization ||
  item?.label ||
  `Organization ${getId(item)}`;

const getBranchName = (item) =>
  item?.name ||
  item?.branch_name ||
  item?.branch ||
  item?.label ||
  `Branch ${getId(item)}`;

const getMemberName = (item) =>
  item?.display_name ||
  item?.member_name ||
  item?.student_name ||
  item?.employee_name ||
  item?.name ||
  item?.admission_name ||
  item?.admission_no ||
  item?.employee_code ||
  `Member ${getId(item)}`;

const getBookName = (item) =>
  item?.display_name ||
  item?.title ||
  item?.book_title ||
  item?.book_name ||
  item?.book_code ||
  `Book ${getId(item)}`;

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-GB");
};

const formatCellValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "object"
            ? JSON.stringify(item)
            : String(item)
        )
        .join(", ");
    }

    return JSON.stringify(value);
  }

  return String(value);
};

const getErrorMessage = (error) => {
  if (error?.response?.status === 401) {
    return "Authentication failed. Please login again.";
  }

  if (error?.response?.status === 403) {
    return "You do not have permission to access this report.";
  }

  if (error?.response?.status === 404) {
    return "Report API endpoint not found.";
  }

  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};

/* =========================================================
   HEX TO RGB
========================================================= */

const hexToRgb = (hex) => {
  if (
    !hex ||
    typeof hex !== "string"
  ) {
    return [0, 0, 0];
  }

  let cleanHex = hex.replace("#", "");

  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (cleanHex.length !== 6) {
    return [0, 0, 0];
  }

  const r = parseInt(
    cleanHex.substring(0, 2),
    16
  );

  const g = parseInt(
    cleanHex.substring(2, 4),
    16
  );

  const b = parseInt(
    cleanHex.substring(4, 6),
    16
  );

  return [
    Number.isNaN(r) ? 0 : r,
    Number.isNaN(g) ? 0 : g,
    Number.isNaN(b) ? 0 : b,
  ];
};

/* =========================================================
   INITIAL FILTERS
========================================================= */

const getInitialFilters = (user) => ({
  organization:
    user?.organization_id || "",

  library_member: "",
  member_type: "",
  book: "",

  transaction_status: "",
  fine_status: "",

  reservation_status: "",

  issue_from: "",
  issue_to: "",

  return_from: "",
  return_to: "",

  reservation_from: "",
  reservation_to: "",

  expiry_from: "",
  expiry_to: "",

  category: "",

  course: "",
  batch: "",

  language: "",
  status: "",
  author: "",
  publisher: "",
  book_code: "",
  title: "",

  minimum_fine: "",
  maximum_fine: "",
});

/* =========================================================
   REPORT CONFIG
========================================================= */

const REPORT_CONFIG = {
  "book-issue": {
    label: "Book Issue Report",
    shortLabel: "Book Issue",
    columns: [
      "transaction_code",
      "member_code",
      "member_name",
      "member_type",
      "book_code",
      "book_title",
      "issue_date",
      "due_date",
      "return_date",
      "overdue_days",
      "fine_amount",
      "fine_status",
      "transaction_status",
      "issued_by",
      "received_by",
    ],
  },

  "book-reservation": {
    label: "Book Reservation Report",
    shortLabel: "Reservations",
    columns: [
      "reservation_code",
      "member_code",
      "member_name",
      "member_type",
      "book_code",
      "book_title",
      "reservation_date",
      "expiry_date",
      "priority_number",
      "reservation_status",
      "notification_sent",
      "remarks",
    ],
  },

  "book-stock": {
    label: "Book Stock Report",
    shortLabel: "Book Stock",
    columns: [
      "book_code",
      "title",
      "isbn",
      "author",
      "publisher",
      "language",
      "organization_name",
      "branch_name",
      "category_name",
      "total_copies",
      "available_copies",
      "issued_copies",
      "reserved_copies",
      "available_status",
      "status",
    ],
  },

  "fine-collection": {
    label: "Fine Collection Report",
    shortLabel: "Fine Collection",
    columns: [
      "transaction_code",
      "member_code",
      "member_name",
      "member_type",
      "book_code",
      "book_title",
      "issue_date",
      "due_date",
      "return_date",
      "overdue_days",
      "fine_amount",
      "fine_status",
      "transaction_status",
    ],
  },

  "member-activity": {
    label: "Library Member Activity Report",
    shortLabel: "Member Activity",
    columns: [
      "id",
      "member_code",
      "member_name",
      "member_type",
      "books_issued",
      "books_returned",
      "currently_issued",
      "reserved_books",
      "pending_fine",
      "paid_fine",
      "total_fine",
      "last_issue_date",
      "last_return_date",
    ],
  },
};

/* =========================================================
   COMPONENT
========================================================= */

export default function LibraryReports() {
  const theme = useTheme();

  const {
    pdfThemeColor,
    pdfTextColor,
  } = usePdfSettings();

  const user = useMemo(
    () => getStoredUser(),
    []
  );

  /* =========================================================
     GLOBAL BRANCH CONTEXT
  ========================================================= */

  const {
    selectedBranchId,
    branchList: globalBranchList = [],
    organizationId: globalOrganizationId,
  } = useBranch();

  /* =========================================================
     PDF SETTINGS
  ========================================================= */

  const [
    showColorPickerModal,
    setShowColorPickerModal,
  ] = useState(false);

  const [
    tempPdfThemeColor,
    setTempPdfThemeColor,
  ] = useState(
    pdfThemeColor || DEFAULT_PDF_COLORS.primary
  );

  const [
    tempPdfTextColor,
    setTempPdfTextColor,
  ] = useState(
    pdfTextColor || DEFAULT_PDF_COLORS.bodyText
  );

  /* =========================================================
     REPORT STATES
  ========================================================= */

  const [reportType, setReportType] =
    useState("book-stock");

  const [filters, setFilters] =
    useState(
      getInitialFilters(user)
    );

  const [organizations, setOrganizations] =
    useState([]);

  const [members, setMembers] =
    useState([]);

  const [books, setBooks] =
    useState([]);

  const [categories, setCategories] =
    useState([]);

  const [rows, setRows] =
    useState([]);

  const [summary, setSummary] =
    useState(null);

  const [pdfRows, setPdfRows] =
    useState([]);

  const [pdfSummary, setPdfSummary] =
    useState(null);

  const latestReportRef = useRef({
    rows: [],
    summary: null,
  });

  const [loading, setLoading] =
    useState(false);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [excelLoading, setExcelLoading] =
    useState(false);

  const [filterOpen, setFilterOpen] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [page, setPage] =
    useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  const [error, setError] =
    useState("");

  const [snackbar, setSnackbar] =
    useState({
      open: false,
      message: "",
      severity: "success",
    });

  /* =========================================================
     DYNAMIC BRANCH PDF DETAILS
  ========================================================= */

  const [branchDetails, setBranchDetails] =
    useState(null);

  const [branchName, setBranchName] =
    useState("");

  const [branchLogo, setBranchLogo] =
    useState("");

  /* =========================================================
     CURRENT REPORT
  ========================================================= */

  const reportConfig =
    REPORT_CONFIG[reportType];

  /* =========================================================
     PDF COLOR MODAL SYNC
  ========================================================= */

  useEffect(() => {
    if (showColorPickerModal) {
      setTempPdfThemeColor(
        pdfThemeColor || DEFAULT_PDF_COLORS.primary
      );

      setTempPdfTextColor(
        pdfTextColor || DEFAULT_PDF_COLORS.bodyText
      );
    }
  }, [
    showColorPickerModal,
    pdfThemeColor,
    pdfTextColor,
  ]);

  /* =========================================================
     SNACKBAR
  ========================================================= */

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  /* =========================================================
     ORGANIZATIONS
  ========================================================= */

  const fetchOrganizations =
    async () => {
      try {
        const response =
          await API.get(
            ORGANIZATION_URL
          );

        setOrganizations(
          normalizeArray(
            response.data
          )
        );
      } catch (error) {
        console.error(
          "Organization API:",
          error?.response?.data
        );
      }
    };

  /* =========================================================
     MEMBERS
  ========================================================= */

  const fetchMembers = async () => {
    if (!filters.organization) {
      setMembers([]);
      return;
    }

    try {
      const params = {
        organization:
          filters.organization,
      };

      if (selectedBranchId) {
        params.branch =
          selectedBranchId;
      }

      if (filters.member_type) {
        params.member_type =
          filters.member_type;
      }

      const response =
        await API.get(
          MEMBER_DROPDOWN_URL,
          {
            params,
          }
        );

      setMembers(
        normalizeArray(
          response.data
        )
      );
    } catch (error) {
      console.error(
        "Member dropdown API:",
        error?.response?.data
      );

      setMembers([]);
    }
  };

  /* =========================================================
     BOOKS
  ========================================================= */

  const fetchBooks = async () => {
    if (!filters.organization) {
      setBooks([]);
      return;
    }

    try {
      const params = {
        organization:
          filters.organization,
      };

      if (selectedBranchId) {
        params.branch =
          selectedBranchId;
      }

      const response =
        await API.get(
          BOOK_DROPDOWN_URL,
          {
            params,
          }
        );

      setBooks(
        normalizeArray(
          response.data
        )
      );
    } catch (error) {
      console.error(
        "Book dropdown API:",
        error?.response?.data
      );

      setBooks([]);
    }
  };

  /* =========================================================
     CATEGORIES
  ========================================================= */

  const fetchCategoriesFromBookMaster =
    async () => {
      if (!filters.organization) {
        setCategories([]);
        return;
      }

      try {
        const params = {
          organization:
            filters.organization,
        };

        if (selectedBranchId) {
          params.branch =
            selectedBranchId;
        }

        const response =
          await API.get(
            BOOK_MASTER_LIST_URL,
            {
              params,
            }
          );

        const bookList =
          normalizeArray(
            response.data
          );

        const map = new Map();

        bookList.forEach(
          (book) => {
            const categoryId =
              book?.category_id ??
              book?.category?.id ??
              book?.category;

            const categoryName =
              book?.category_name ??
              book?.category?.name;

            if (
              categoryId !==
                undefined &&
              categoryId !== null &&
              categoryName
            ) {
              map.set(
                String(
                  categoryId
                ),
                {
                  id: categoryId,
                  name: categoryName,
                }
              );
            }
          }
        );

        setCategories(
          Array.from(
            map.values()
          )
        );
      } catch (error) {
        console.error(
          "Category extraction error:",
          error?.response?.data
        );

        setCategories([]);
      }
    };

  /* =========================================================
     INITIAL ORGANIZATION
  ========================================================= */

  useEffect(() => {
    fetchOrganizations();
  }, []);

  /* =========================================================
     SYNC GLOBAL ORGANIZATION
     BRANCH COMES ONLY FROM GLOBAL BRANCH CONTEXT
  ========================================================= */

  useEffect(() => {
    const effectiveOrganizationId =
      globalOrganizationId ||
      user?.organization_id ||
      user?.organization?.id ||
      user?.organization ||
      "";

    setFilters((prev) => {
      if (
        String(prev.organization || "") ===
        String(effectiveOrganizationId || "")
      ) {
        return prev;
      }

      return {
        ...prev,
        organization: effectiveOrganizationId,
      };
    });
  }, [
    globalOrganizationId,
    user,
  ]);

  /* =========================================================
     DYNAMIC BRANCH DETAILS
  ========================================================= */

  const getAbsoluteImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("data:") ||
      image.startsWith("blob:") ||
      image.startsWith("http://") ||
      image.startsWith("https://")
    ) {
      return image;
    }

    const cleanPath =
      image.startsWith("/")
        ? image
        : `/${image}`;

    return `https://wims.revisetoexcel.com${cleanPath}`;
  };

  const imageUrlToDataUrl = async (
    imageUrl
  ) => {
    if (!imageUrl) {
      return "";
    }

    try {
      const response =
        await API.get(
          imageUrl,
          {
            responseType: "blob",
          }
        );

      return await new Promise(
        (resolve) => {
          const reader =
            new FileReader();

          reader.onloadend = () =>
            resolve(
              reader.result || ""
            );

          reader.onerror = () =>
            resolve("");

          reader.readAsDataURL(
            response.data
          );
        }
      );
    } catch (error) {
      console.error(
        "Branch logo conversion error:",
        error?.response?.data ||
          error
      );

      return "";
    }
  };

  useEffect(() => {
    const effectiveBranchId =
      selectedBranchId ||
      "";

    const fetchBranchDetails =
      async () => {
        if (!effectiveBranchId) {
          setBranchDetails(null);
          setBranchName("");
          setBranchLogo("");
          return;
        }

        try {
          const response =
            await API.get(
              `/master/branch/${effectiveBranchId}/`
            );

          const data =
            response?.data || {};

          const name =
            data?.name ||
            data?.branch_name ||
            data?.branchName ||
            "";

          const image =
            data?.image ||
            data?.logo ||
            data?.branch_logo ||
            data?.branchLogo ||
            "";

          const absoluteLogo =
            getAbsoluteImageUrl(
              image
            );

          setBranchDetails(data);
          setBranchName(name);
          setBranchLogo(
            absoluteLogo
          );
        } catch (error) {
          console.error(
            "Branch details API:",
            error?.response?.data ||
              error
          );

          setBranchDetails(null);
          setBranchName("");
          setBranchLogo("");
        }
      };

    fetchBranchDetails();
  }, [
    selectedBranchId,
  ]);

  /* =========================================================
     DROPDOWN DATA
  ========================================================= */

  useEffect(() => {
    if (!filters.organization) {
      return;
    }

    fetchMembers();
    fetchBooks();
    fetchCategoriesFromBookMaster();
  }, [
    filters.organization,
    selectedBranchId,
    filters.member_type,
  ]);

  /* =========================================================
     FILTER CHANGE
  ========================================================= */

  const handleFilterChange = (
    name,
    value
  ) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (
        name ===
        "organization"
      ) {
        next.library_member =
          "";
        next.book = "";
        next.category = "";
      }

      return next;
    });

    setPage(0);
  };

  /* =========================================================
     BUILD REPORT PARAMS
  ========================================================= */

  const buildReportParams = (
    customFilters = filters
  ) => {
    const params = {};

    Object.entries(
      customFilters
    ).forEach(
      ([key, value]) => {
        if (
          value !== "" &&
          value !== null &&
          value !== undefined
        ) {
          params[key] = value;
        }
      }
    );

    /* =====================================================
       GLOBAL BRANCH CONTEXT
       Always send the globally selected branch to the
       report API through the `branch` query parameter.
    ===================================================== */

    const effectiveOrganizationId =
      globalOrganizationId ||
      customFilters?.organization ||
      user?.organization_id ||
      "";

    const effectiveBranchId =
      selectedBranchId ||
      "";

    if (effectiveOrganizationId) {
      params.organization =
        effectiveOrganizationId;
    }

    if (effectiveBranchId) {
      params.branch =
        effectiveBranchId;
    } else {
      delete params.branch;
    }

    return params;
  };

  /* =========================================================
     FETCH REPORT
  ========================================================= */

  const fetchReport = async (
    customFilters = filters
  ) => {
    try {
      setLoading(true);
      setError("");

      const endpoint =
        REPORT_ENDPOINTS[
          reportType
        ];

      const params =
        buildReportParams(
          customFilters
        );

      const response =
        await API.get(
          endpoint,
          {
            params,
          }
        );

      const data =
        response.data;

      const result =
        normalizeArray(data);

      const reportSummary =
        data?.summary ||
        data?.metadata ||
        null;

      latestReportRef.current = {
        rows: result,
        summary: reportSummary,
      };

      setRows(result);

      setSummary(
        reportSummary
      );

      setPage(0);
      setFilterOpen(false);
      return result;
    } catch (error) {
      console.error(
        "Report API Error:",
        error?.response?.data
      );

      const message =
        getErrorMessage(error);

      setError(message);

      latestReportRef.current = {
        rows: [],
        summary: null,
      };

      setRows([]);
      setSummary(null);

      showSnackbar(
        message,
        "error"
      );
      return [];
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL REPORT
  ========================================================= */

  useEffect(() => {
    if (
      filters.organization
    ) {
      fetchReport();
    }
  }, [
    reportType,
    filters.organization,
    selectedBranchId,
    globalOrganizationId,
  ]);

  /* =========================================================
     SEARCH
  ========================================================= */

  const filteredRows =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase();

      if (!term) {
        return rows;
      }

      return rows.filter(
        (row) =>
          Object.values(
            row || {}
          ).some(
            (value) =>
              formatCellValue(
                value
              )
                .toLowerCase()
                .includes(term)
          )
      );
    }, [
      rows,
      search,
    ]);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const paginatedRows =
    useMemo(() => {
      const start =
        page *
        rowsPerPage;

      return filteredRows.slice(
        start,
        start +
          rowsPerPage
      );
    }, [
      filteredRows,
      page,
      rowsPerPage,
    ]);

  /* =========================================================
     RESET
  ========================================================= */

  const handleReset = () => {
    const nextFilters = {
      ...getInitialFilters(user),
      organization:
        globalOrganizationId ||
        user?.organization_id ||
        user?.organization?.id ||
        user?.organization ||
        "",
    };

    setFilters(nextFilters);
    setSearch("");
    setPage(0);
    setError("");
    setPdfRows([]);
    setPdfSummary(null);

    if (nextFilters.organization) {
      setTimeout(() => {
        fetchReport(nextFilters);
      }, 0);
    } else {
      setRows([]);
      setSummary(null);
    }
  };

  /* =========================================================
     ACTIVE FILTER COUNT
  ========================================================= */

  const activeFilterCount =
    Object.entries(
      filters
    ).filter(
      ([key, value]) =>
        key !== "organization" &&
        value !== "" &&
        value !== null &&
        value !== undefined
    ).length;

  /* =========================================================
     REPORT COLUMNS
  ========================================================= */

  const tableColumns =
    useMemo(() => {
      if (rows.length === 0) {
        return reportConfig.columns;
      }

      const keys =
        new Set();

      rows.forEach(
        (row) => {
          Object.keys(
            row || {}
          ).forEach(
            (key) =>
              keys.add(key)
          );
        }
      );

      const preferred =
        reportConfig.columns.filter(
          (key) =>
            keys.has(key)
        );

      const remaining =
        Array.from(
          keys
        ).filter(
          (key) =>
            !preferred.includes(
              key
            )
        );

      return [
        ...preferred,
        ...remaining,
      ];
    }, [
      rows,
      reportConfig.columns,
    ]);

  /* =========================================================
     COLUMN LABEL
  ========================================================= */

  const getColumnLabel = (
    key
  ) => {
    return key
      .replace(
        /_/g,
        " "
      )
      .replace(
        /\b\w/g,
        (char) =>
          char.toUpperCase()
      );
  };

  /* =========================================================
     GENERATE PDF
  ========================================================= */

  const generatePDF = async (
    themeColor =
      tempPdfThemeColor ||
      DEFAULT_PDF_COLORS.primary,
    textColor =
      tempPdfTextColor ||
      DEFAULT_PDF_COLORS.bodyText
  ) => {
    try {
      setPdfLoading(true);

      const reportRows =
        Array.isArray(pdfRows)
          ? pdfRows
          : [];

      const reportSummary =
        pdfSummary || summary;

      if (!Array.isArray(reportRows) || !reportRows.length) {
        showSnackbar(
          "No report data available for PDF. Please generate the report first.",
          "warning"
        );

        return false;
      }

      const doc =
        new jsPDF({
          orientation:
            "landscape",
          unit: "mm",
          format: "a4",
        });

      const pageWidth =
        doc.internal.pageSize.getWidth();

      const pageHeight =
        doc.internal.pageSize.getHeight();

      const margin = 12;

      const themeRgb =
        hexToRgb(
          themeColor
        );

      const textRgb =
        hexToRgb(
          textColor
        );

      /* =====================================================
         DYNAMIC GLOBAL BRANCH DETAILS FOR PDF
      ===================================================== */

      const currentBranchName =
        branchName ||
        displayBranchName ||
        reportRows?.[0]?.branch_name ||
        "";

      const currentOrganizationName =
        reportRows?.[0]?.organization_name ||
        organizationName ||
        branchDetails?.organization_name ||
        "";

      const currentBranchLogo =
        branchLogo ||
        "";

      let pdfLogo = "";

      if (currentBranchLogo) {
        pdfLogo =
          await imageUrlToDataUrl(
            currentBranchLogo
          );
      }

      /* =====================================================
         PDF HEADER
         Organization + branch + report title are centered.
         The logo is fetched from the selected global branch.
      ===================================================== */

      const drawPdfHeader = () => {
        doc.setFillColor(
          themeRgb[0],
          themeRgb[1],
          themeRgb[2]
        );

        doc.rect(
          0,
          0,
          pageWidth,
          31,
          "F"
        );

        if (pdfLogo) {
          try {
            doc.addImage(
              pdfLogo,
              "PNG",
              margin,
              5,
              18,
              18,
              undefined,
              "FAST"
            );
          } catch (logoError) {
            console.error(
              "Unable to add branch logo to PDF:",
              logoError
            );
          }
        }

        const headerTextRgb =
          hexToRgb(
            DEFAULT_PDF_COLORS.headerText
          );

        doc.setTextColor(
          headerTextRgb[0],
          headerTextRgb[1],
          headerTextRgb[2]
        );

        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(16);

        doc.text(
          currentOrganizationName ||
            "Organization",
          pageWidth / 2,
          9,
          {
            align: "center",
          }
        );

        doc.setFontSize(10);

        doc.text(
          currentBranchName ||
            "Branch",
          pageWidth / 2,
          15,
          {
            align: "center",
          }
        );

        doc.setFontSize(11);

        doc.text(
          reportConfig.label,
          pageWidth / 2,
          23,
          {
            align: "center",
          }
        );

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(7.5);

        doc.text(
          `Generated: ${new Date().toLocaleString()}`,
          pageWidth - margin,
          25,
          {
            align: "right",
          }
        );
      };

      drawPdfHeader();

      /* =====================================================
         FILTER SUMMARY
      ===================================================== */

      let y = 39;

      doc.setTextColor(
        textRgb[0],
        textRgb[1],
        textRgb[2]
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(10);

      doc.text(
        "FILTERS",
        margin,
        y
      );

      y += 6;

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(8);

      const filterText =
        Object.entries(
          filters
        )
          .filter(
            ([key, value]) =>
              key !== "branch" &&
              value !== "" &&
              value !== null &&
              value !== undefined
          )
          .map(
            ([key, value]) =>
              `${getColumnLabel(
                key
              )}: ${value}`
          )
          .join(
            "   |   "
          );

      const filterLines =
        doc.splitTextToSize(
          filterText ||
            "All filters",
          pageWidth -
            margin * 2
        );

      doc.text(
        filterLines,
        margin,
        y
      );

      y +=
        filterLines.length *
          4 +
        6;

      /* =====================================================
         SUMMARY
      ===================================================== */

      if (
        reportSummary &&
        typeof reportSummary ===
          "object"
      ) {
        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(10);

        doc.text(
          "SUMMARY",
          margin,
          y
        );

        y += 5;

        const summaryEntries =
          Object.entries(
            reportSummary
          ).filter(
            ([, value]) =>
              typeof value !==
                "object" &&
              value !== null
          );

        if (
          summaryEntries.length
        ) {
          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(8);

          const summaryText =
            summaryEntries
              .map(
                ([key, value]) =>
                  `${getColumnLabel(
                    key
                  )}: ${value}`
              )
              .join(
                "    |    "
              );

          doc.text(
            summaryText,
            margin,
            y
          );

          y += 7;
        }
      }

      /* =====================================================
         TABLE
      ===================================================== */

      const pdfColumns =
        tableColumns;

      const pdfHead =
        pdfColumns.map(
          getColumnLabel
        );

      const pdfBody =
        reportRows.map(
          (row) =>
            pdfColumns.map(
              (column) =>
                formatCellValue(
                  row?.[
                    column
                  ]
                )
            )
        );

      autoTable(doc, {
        startY:
          y + 2,

        head: [
          pdfHead,
        ],

        body:
          pdfBody,

        theme: "grid",

        styles: {
          font:
            "helvetica",

          fontSize: 7,

          cellPadding: 2,

          lineColor:
            hexToRgb(
              DEFAULT_PDF_COLORS.border
            ),

          lineWidth: 0.1,

          textColor:
            textRgb,

          valign:
            "middle",

          halign:
            "center",
        },

        headStyles: {
          fillColor:
            themeRgb,

          textColor:
            hexToRgb(
              DEFAULT_PDF_COLORS.tableHeaderText
            ),

          fontStyle:
            "bold",

          halign:
            "center",

          valign:
            "middle",

          fontSize:
            7.5,
        },

        alternateRowStyles: {
          fillColor:
            hexToRgb(
              DEFAULT_PDF_COLORS.alternateRow
            ),
        },

        didParseCell: (
          data
        ) => {
          data.cell.styles.valign =
            "middle";

          data.cell.styles.halign =
            "center";

          if (
            data.section ===
            "body"
          ) {
            data.cell.styles.textColor =
              textRgb;
          }
        },

        margin: {
          top: 38,
          left: margin,
          right: margin,
          bottom: 12,
        },

        willDrawPage: () => {
          drawPdfHeader();
        },

        didDrawPage: () => {
          const currentPage =
            doc.internal.getNumberOfPages();

          doc.setFontSize(7);

          const mutedTextRgb =
            hexToRgb(
              DEFAULT_PDF_COLORS.mutedText
            );

          doc.setTextColor(
            mutedTextRgb[0],
            mutedTextRgb[1],
            mutedTextRgb[2]
          );

          doc.text(
            `Page ${currentPage}`,
            pageWidth - margin,
            pageHeight - 5,
            { align: "right" }
          );

          doc.text(
            "Library Reports",
            margin,
            pageHeight - 5
          );
        },
      });

      const fileName =
        `${reportType.replace(
          /-/g,
          "_"
        )}_report_${new Date()
          .toISOString()
          .slice(
            0,
            10
          )}.pdf`;

      /* =====================================================
         OPEN PDF
      ===================================================== */

      const pdfBlob =
        doc.output(
          "blob"
        );

      const pdfUrl =
        URL.createObjectURL(
          pdfBlob
        );

      const newWindow =
        window.open(
          pdfUrl,
          "_blank"
        );

      if (
        !newWindow ||
        newWindow.closed ||
        typeof newWindow.closed ===
          "undefined"
      ) {
        const link =
          document.createElement(
            "a"
          );

        link.href =
          pdfUrl;

        link.download =
          fileName;

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );
      }

      setTimeout(
        () =>
          URL.revokeObjectURL(
            pdfUrl
          ),
        10000
      );

      showSnackbar(
        "PDF generated successfully.",
        "success"
      );
      return true;
    } catch (error) {
      console.error(
        "PDF generation error:",
        error
      );

      showSnackbar(
        getErrorMessage(
          error
        ),
        "error"
      );
      return false;
    } finally {
      setPdfLoading(
        false
      );
    }
  };

  /* =========================================================
     OPEN PDF COLOR PICKER
  ========================================================= */

  const handleGenerateReport =
    async () => {
      const refreshedRows =
        await fetchReport(filters);

      if (
        !Array.isArray(refreshedRows) ||
        !refreshedRows.length
      ) {
        setPdfRows([]);
        setPdfSummary(null);

        showSnackbar(
          "No report data found for the selected filters.",
          "warning"
        );

        return;
      }

      setPdfRows(
        refreshedRows
      );

      setPdfSummary(
        latestReportRef.current.summary || null
      );

      setTempPdfThemeColor(
        pdfThemeColor || DEFAULT_PDF_COLORS.primary
      );

      setTempPdfTextColor(
        pdfTextColor || DEFAULT_PDF_COLORS.bodyText
      );

      setShowColorPickerModal(
        true
      );
    };

  /* =========================================================
     EXCEL EXPORT
  ========================================================= */

  const exportExcel = async () => {
    try {
      setExcelLoading(true);

      const reportRows =
        rows || [];

      if (!reportRows.length) {
        showSnackbar(
          "No report data available to export. Please generate the report first.",
          "warning"
        );

        return;
      }

      const exportRows =
        reportRows.map(
          (row, index) => {
            const result = {
              "S.No.": index + 1,
            };

            tableColumns.forEach(
              (column) => {
                const value =
                  row?.[column];

                const lowerColumn =
                  column.toLowerCase();

                const isDate =
                  lowerColumn.includes("date") ||
                  lowerColumn.includes("_from") ||
                  lowerColumn.includes("_to");

                result[
                  getColumnLabel(column)
                ] = isDate
                  ? formatDate(value)
                  : formatCellValue(value);
              }
            );

            return result;
          }
        );

      const worksheet =
        XLSX.utils.json_to_sheet(
          exportRows
        );

      const columnWidths =
        Object.keys(
          exportRows[0] || {}
        ).map((key) => {
          const maxLength =
            exportRows.reduce(
              (max, row) =>
                Math.max(
                  max,
                  String(
                    row?.[key] ?? ""
                  ).length
                ),
              key.length
            );

          return {
            wch: Math.min(
              Math.max(
                maxLength + 2,
                12
              ),
              40
            ),
          };
        });

      worksheet["!cols"] =
        columnWidths;

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Report"
      );

      const fileName =
        `${reportType.replace(
          /-/g,
          "_"
        )}_report_${new Date()
          .toISOString()
          .slice(
            0,
            10
          )}.xlsx`;

      XLSX.writeFile(
        workbook,
        fileName
      );

      showSnackbar(
        "Excel exported successfully.",
        "success"
      );
    } catch (error) {
      console.error(
        "Excel export error:",
        error
      );

      showSnackbar(
        getErrorMessage(error),
        "error"
      );
    } finally {
      setExcelLoading(false);
    }
  };

  /* =========================================================
     FILTER FIELD HELPERS
  ========================================================= */

  const renderSelect = ({
    label,
    name,
    value,
    options,
    disabled = false,
  }) => (
    <FormControl
      fullWidth
      size="small"
      disabled={disabled}
    >
      <InputLabel>
        {label}
      </InputLabel>

      <Select
        value={
          value ?? ""
        }
        label={
          label
        }
        onChange={(
          event
        ) =>
          handleFilterChange(
            name,
            event.target.value
          )
        }
      >
        <MenuItem value="">
          All
        </MenuItem>

        {options.map(
          (
            option
          ) => (
            <MenuItem
              key={
                option.value
              }
              value={
                option.value
              }
            >
              {
                option.label
              }
            </MenuItem>
          )
        )}
      </Select>
    </FormControl>
  );

  const renderDate = (
    label,
    name
  ) => (
    <TextField
      fullWidth
      size="small"
      type="date"
      label={
        label
      }
      InputLabelProps={{
        shrink:
          true,
      }}
      value={
        filters[
          name
        ] || ""
      }
      onChange={(
        event
      ) =>
        handleFilterChange(
          name,
          event.target.value
        )
      }
    />
  );

  /* =========================================================
     FILTER CONTENT
  ========================================================= */

  const renderFilters = () => {
    return (
    <Box
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, 1fr)",
    },
    gap: 2,
  }}
>
        {/* =====================================================
            MEMBER / BOOK
        ===================================================== */}

        {[
          "book-issue",
          "book-reservation",
          "fine-collection",
        ].includes(
          reportType
        ) && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Library Member",
                name:
                  "library_member",
                value:
                  filters.library_member,
                disabled:
                  !filters.organization,
                options:
                  members.map(
                    (
                      item
                    ) => ({
                      value:
                        getId(
                          item
                        ),
                      label:
                        getMemberName(
                          item
                        ),
                    })
                  ),
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Member Type",
                name:
                  "member_type",
                value:
                  filters.member_type,
                options: [
                  {
                    value:
                      "student",
                    label:
                      "Student",
                  },
                  {
                    value:
                      "staff",
                    label:
                      "Staff",
                  },
                ],
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Book",
                name:
                  "book",
                value:
                  filters.book,
                disabled:
                  !filters.organization,
                options:
                  books.map(
                    (
                      item
                    ) => ({
                      value:
                        getId(
                          item
                        ),
                      label:
                        getBookName(
                          item
                        ),
                    })
                  ),
              })}
            </Grid>
          </>
        )}

        {/* =====================================================
            BOOK ISSUE
        ===================================================== */}

        {reportType ===
          "book-issue" && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Transaction Status",
                name:
                  "transaction_status",
                value:
                  filters.transaction_status,
                options: [
                  {
                    value:
                      "issued",
                    label:
                      "Issued",
                  },
                  {
                    value:
                      "returned",
                    label:
                      "Returned",
                  },
                ],
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Fine Status",
                name:
                  "fine_status",
                value:
                  filters.fine_status,
                options: [
                  {
                    value:
                      "pending",
                    label:
                      "Pending",
                  },
                  {
                    value:
                      "paid",
                    label:
                      "Paid",
                  },
                  {
                    value:
                      "waived",
                    label:
                      "Waived",
                  },
                ],
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Issue Date From",
                "issue_from"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Issue Date To",
                "issue_to"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Return Date From",
                "return_from"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Return Date To",
                "return_to"
              )}
            </Grid>
          </>
        )}

        {/* =====================================================
            BOOK RESERVATION
        ===================================================== */}

        {reportType ===
          "book-reservation" && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Reservation Status",
                name:
                  "reservation_status",
                value:
                  filters.reservation_status,
                options: [
                  {
                    value:
                      "pending",
                    label:
                      "Pending",
                  },
                  {
                    value:
                      "ready",
                    label:
                      "Ready",
                  },
                  {
                    value:
                      "fulfilled",
                    label:
                      "Fulfilled",
                  },
                  {
                    value:
                      "cancelled",
                    label:
                      "Cancelled",
                  },
                ],
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Reservation Date From",
                "reservation_from"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Reservation Date To",
                "reservation_to"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Expiry Date From",
                "expiry_from"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Expiry Date To",
                "expiry_to"
              )}
            </Grid>
          </>
        )}

        {/* =====================================================
            BOOK STOCK
        ===================================================== */}

        {reportType ===
          "book-stock" && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Category",
                name:
                  "category",
                value:
                  filters.category,
                options:
                  categories.map(
                    (
                      item
                    ) => ({
                      value:
                        getId(
                          item
                        ),
                      label:
                        item.name,
                    })
                  ),
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                label="Course"
                value={
                  filters.course
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "course",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                label="Batch"
                value={
                  filters.batch
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "batch",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                label="Language"
                value={
                  filters.language
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "language",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Book Status",
                name:
                  "status",
                value:
                  filters.status,
                options: [
                  {
                    value:
                      "active",
                    label:
                      "Active",
                  },
                  {
                    value:
                      "inactive",
                    label:
                      "Inactive",
                  },
                ],
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                label="Author"
                value={
                  filters.author
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "author",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                label="Publisher"
                value={
                  filters.publisher
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "publisher",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                label="Book Code"
                value={
                  filters.book_code
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "book_code",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                label="Book Title"
                value={
                  filters.title
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "title",
                    event.target
                      .value
                  )
                }
              />
            </Grid>
          </>
        )}

        {/* =====================================================
            FINE COLLECTION
        ===================================================== */}

        {reportType ===
          "fine-collection" && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              {renderSelect({
                label:
                  "Fine Status",
                name:
                  "fine_status",
                value:
                  filters.fine_status,
                options: [
                  {
                    value:
                      "pending",
                    label:
                      "Pending",
                  },
                  {
                    value:
                      "paid",
                    label:
                      "Paid",
                  },
                  {
                    value:
                      "waived",
                    label:
                      "Waived",
                  },
                ],
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Minimum Fine"
                value={
                  filters.minimum_fine
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "minimum_fine",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Maximum Fine"
                value={
                  filters.maximum_fine
                }
                onChange={(
                  event
                ) =>
                  handleFilterChange(
                    "maximum_fine",
                    event.target
                      .value
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Issue Date From",
                "issue_from"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Issue Date To",
                "issue_to"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Return Date From",
                "return_from"
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              {renderDate(
                "Return Date To",
                "return_to"
              )}
            </Grid>
          </>
        )}
      </Box>
    );
  };

  /* =========================================================
     REPORT TAB CHANGE
  ========================================================= */

  const handleReportChange = (
    newReport
  ) => {
    if (
      newReport ===
      reportType
    ) {
      return;
    }

    setReportType(
      newReport
    );

    setRows([]);
    setSummary(null);
    setSearch("");
    setPage(0);
    setError("");
  };

  /* =========================================================
     ORGANIZATION NAME
  ========================================================= */

  const selectedOrganization =
    organizations.find(
      (item) =>
        String(
          getId(item)
        ) ===
        String(
          filters.organization
        )
    );

  const organizationName =
    selectedOrganization
      ? getOrganizationName(
          selectedOrganization
        )
      : user?.organization_name ||
        user?.organization?.name ||
        "All Organizations";

  /* =========================================================
     SELECTED GLOBAL BRANCH
  ========================================================= */

  const selectedGlobalBranch =
    globalBranchList.find(
      (item) =>
        String(
          getId(item)
        ) ===
        String(
          selectedBranchId ||
            ""
        )
    );

  const displayBranchName =
    branchName ||
    (selectedGlobalBranch
      ? getBranchName(
          selectedGlobalBranch
        )
      : branchName || "");

  /* =========================================================
     TABLE CELL DISPLAY
  ========================================================= */

  const renderTableValue = (
    column,
    value
  ) => {
    const lower =
      column.toLowerCase();

    if (
      lower.includes(
        "date"
      )
    ) {
      return formatDate(
        value
      );
    }

    return formatCellValue(
      value
    );
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <Box
      
      disableGutters
      sx={{
        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },
        py: {
          xs: 1.5,
          md: 2.5,
        },
      }}
    >
      {/* =====================================================
          REPORT TABS
      ===================================================== */}

    {/* =====================================================
    REPORT TABS
===================================================== */}

<Box
  sx={{
    mb: 2,
    p: 0.5,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflowX: "auto",
  }}
>
  <Tabs
    value={reportType}
    onChange={(_event, value) =>
      handleReportChange(value)
    }
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 42,

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: {
          xs: 120,
          sm: 145,
        },
        px: 2,
        borderRadius: 1.5,
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.875rem",
        color: "text.primary",
        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          color: "text.primary",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },
    }}
  >
    {Object.entries(REPORT_CONFIG).map(
      ([value, config]) => (
        <Tab
          key={value}
          value={value}
          label={
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="center"
            >
              <AssessmentIcon
                sx={{
                  fontSize: 18,
                }}
              />

              <span>
                {config.shortLabel}
              </span>
            </Stack>
          }
        />
      )
    )}
  </Tabs>
</Box>

      {/* =====================================================
          REPORT HEADER
      ===================================================== */}

      <Box
        sx={{
          borderRadius: 3,
         
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Box
          sx={{
            p: {
              xs: 1.5,
              sm: 2,
              md: 2.5,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              lg: "row",
            }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{
              xs: "stretch",
              lg: "center",
            }}
          >
           

            {/* =================================================
                ACTIONS
            ================================================= */}
<TextField
              fullWidth
              size="small"
              placeholder="Search report data..."
              value={search}
              onChange={(
                event
              ) => {
                setSearch(
                  event.target.value
                );
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      color="action"
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: {
                  md: 450,
                },
                "& .MuiOutlinedInput-root":
                  {
                    borderRadius: 2,
                  },
              }}
            />
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent={{
                xs: "flex-start",
                lg: "flex-end",
              }}
            >
              <Button
                size="small"
                variant="contained"
                startIcon={
                  <FilterListIcon />
                }
                onClick={() =>
                  setFilterOpen(
                    true
                  )
                }
                sx={{
                  minHeight: 38,
                  borderRadius: 2,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Filters

                {activeFilterCount >
                  0 && (
                  <Chip
                    size="small"
                    label={
                      activeFilterCount
                    }
                    color="primary"
                    sx={{
                      ml: 0.8,
                      height: 20,
                      "& .MuiChip-label":
                        {
                          px: 0.7,
                        },
                    }}
                  />
                )}
              </Button>

              <Button
                size="small"
                variant="contained"
                startIcon={
                  pdfLoading ? (
                    <CircularProgress
                      size={16}
                      color="inherit"
                    />
                  ) : (
                    <PictureAsPdfIcon />
                  )
                }
                disabled={
                  pdfLoading ||
                  loading ||
                  !filters.organization
                }
                onClick={handleGenerateReport}
                sx={{
                  minHeight: 38,
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                }}
              >
                Generate Report
              </Button>

              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={
                  excelLoading ? (
                    <CircularProgress
                      size={16}
                      color="inherit"
                    />
                  ) : (
                    <FileDownloadIcon />
                  )
                }
                disabled={
                  excelLoading ||
                  loading ||
                  !filters.organization
                }
                onClick={
                  exportExcel
                }
                sx={{
                  minHeight: 38,
                  borderRadius: 2,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Export Excel
              </Button>

              <Button
                size="small"
                variant="contained"
                startIcon={
                  <RefreshIcon />
                }
                onClick={
                  handleReset
                }
                sx={{
                  minHeight: 38,
                  borderRadius: 2,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>

       

        {/* =====================================================
            SEARCH / BRANCH TOOLBAR
        ===================================================== */}

        <Box
          sx={{
            px: {
              xs: 1.5,
              sm: 2,
              md: 2.5,
            },
            py: 1.5,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={1.5}
            alignItems={{
              xs: "stretch",
              md: "center",
            }}
          >
            

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Chip
                size="small"
                variant="contained"
                color="primary"
                label={`${filteredRows.length} ${
                  filteredRows.length ===
                  1
                    ? "record"
                    : "records"
                }`}
              />
            </Box>
          </Stack>
        </Box>
      </Box>

  

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
          }}
          onClose={() =>
            setError("")
          }
        >
          {error}
        </Alert>
      )}

      {/* =====================================================
          TABLE CARD
      ===================================================== */}

      <Box
        sx={{
          borderRadius: 3,

         
          overflow: "hidden",
        }}
      >
        {/* ===================================================
            TABLE TOP
        =================================================== */}

        <Box
          sx={{
            px: {
              xs: 1.5,
              md: 2,
            },
            py: 1.25,
            
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 1,
          }}
        >
         

          {search && (
            <Chip
              size="small"
              label={`Search: ${search}`}
              onDelete={() =>
                setSearch("")
              }
              variant="contained"
            />
          )}
        </Box>

        {/* ===================================================
            TABLE
        =================================================== */}

        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <TableContainer
            sx={{
              width: "100%",
              maxHeight: "calc(100vh - 390px)",
              minHeight: 260,
              overflowX: "auto",
              overflowY: "auto",
              borderRadius: 0,
              backgroundColor: (theme) => theme.palette.background.paper,
              "&::-webkit-scrollbar": {
                width: 8,
                height: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 8,
                backgroundColor: (theme) =>
                  theme.palette.action.disabled,
              },
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: Math.max(900, 140 * (tableColumns.length + 1)),
                tableLayout: "auto",
                backgroundColor: (theme) => theme.palette.background.paper,
                "& .MuiTableCell-root": {
                  borderColor: "divider",
                },
                "& .MuiTableBody-root .MuiTableCell-root": {
                  backgroundColor: (theme) => theme.palette.background.paper,
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 4,
                      minWidth: 70,
                      width: 70,
                      py: 1.5,
                      px: 1.5,
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      backgroundColor: (theme) =>
                        theme.palette.primary.main,
                      color: (theme) =>
                        theme.palette.primary.contrastText,
                      borderBottom: "none",
                      "&.MuiTableCell-stickyHeader": {
                        backgroundColor: (theme) =>
                          theme.palette.primary.main,
                        color: (theme) =>
                          theme.palette.primary.contrastText,
                      },
                    }}
                  >
                    S.No.
                  </TableCell>

                  {tableColumns.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        minWidth: 140,
                        py: 1.5,
                        px: 2,
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        whiteSpace: "nowrap",
                        backgroundColor: (theme) =>
                          theme.palette.primary.main,
                        color: (theme) =>
                          theme.palette.primary.contrastText,
                        borderBottom: "none",
                        "&.MuiTableCell-stickyHeader": {
                          backgroundColor: (theme) =>
                            theme.palette.primary.main,
                          color: (theme) =>
                            theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      {getColumnLabel(column)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns.length + 1}
                      align="center"
                      sx={{ py: 9 }}
                    >
                      <Stack alignItems="center" spacing={1.5}>
                        <CircularProgress size={34} />
                        <Typography fontWeight={600}>
                          Loading report...
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Please wait while the report data is being loaded.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : !filters.organization ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns.length + 1}
                      align="center"
                      sx={{ py: 9 }}
                    >
                      <Stack alignItems="center" spacing={1}>
                        <AssessmentIcon
                          sx={{
                            fontSize: 42,
                            color: "text.disabled",
                          }}
                        />
                        <Typography fontWeight={700}>
                          Organization not selected
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Select a valid organization to load report data.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => (
                    <TableRow
                      key={row?.id || `${reportType}-${page}-${index}`}
                      hover
                      sx={{
                        transition: "background-color 0.2s ease",
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                        "&:nth-of-type(even)": {
                          backgroundColor: (theme) =>
                            theme.palette.background.paper,
                        },
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.action.hover,
                        },
                        "& .MuiTableCell-root": {
                          backgroundColor: (theme) =>
                            theme.palette.background.paper,
                        },
                        "&:hover .MuiTableCell-root": {
                          backgroundColor: (theme) =>
                            theme.palette.action.hover,
                        },
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          minWidth: 70,
                          width: 70,
                          py: 1.5,
                          px: 1.5,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "text.primary",
                          backgroundColor: (theme) =>
                            theme.palette.background.paper,
                          whiteSpace: "nowrap",
                          borderColor: "divider",
                          "tr:hover &": {
                            backgroundColor: (theme) =>
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {tableColumns.map((column) => {
                        const value = row?.[column];

                        return (
                          <TableCell
                            key={column}
                            sx={{
                              py: 1.5,
                              px: 2,
                              fontSize: "0.875rem",
                              color: "text.primary",
                              whiteSpace: "nowrap",
                              maxWidth: 300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              borderColor: "divider",
                              backgroundColor: (theme) =>
                                theme.palette.background.paper,
                              "tr:hover &": {
                                backgroundColor: (theme) =>
                                  theme.palette.action.hover,
                              },
                            }}
                            title={formatCellValue(value)}
                          >
                            {renderTableValue(column, value)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns.length + 1}
                      align="center"
                      sx={{ py: 9 }}
                    >
                      <Stack alignItems="center" spacing={1}>
                        <SearchIcon
                          sx={{
                            fontSize: 42,
                            color: "text.disabled",
                          }}
                        />
                        <Typography fontWeight={700}>
                          No report data found
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Try changing the filters or search criteria.
                        </Typography>
                        {search && (
                          <Button size="small" onClick={() => setSearch("")}>
                            Clear Search
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ===================================================
            PAGINATION
        =================================================== */}

        <TablePagination
          component="div"
          count={
            filteredRows.length
          }
          page={
            filteredRows.length ===
            0
              ? 0
              : Math.min(
                  page,
                  Math.max(
                    0,
                    Math.ceil(
                      filteredRows.length /
                        rowsPerPage
                    ) - 1
                  )
                )
          }
          onPageChange={(
            _event,
            newPage
          ) =>
            setPage(
              newPage
            )
          }
          rowsPerPage={
            rowsPerPage
          }
          onRowsPerPageChange={(
            event
          ) => {
            setRowsPerPage(
              Number(
                event.target.value
              )
            );

            setPage(0);
          }}
          rowsPerPageOptions={[
            10,
            25,
            50,
            100,
          ]}
          labelRowsPerPage="Rows:"
          sx={{
            borderTop:
              "1px solid",
          
            borderColor:
              "divider",
            "& .MuiTablePagination-toolbar":
              {
                minHeight: 52,
                px: {
                  xs: 1,
                  sm: 2,
                },
              },
          }}
        />
      </Box>

      {/* =====================================================
          FILTER MODAL
      ===================================================== */}

  {/* =====================================================
    FILTER MODAL
===================================================== */}

<Modal
  open={filterOpen}
  onClose={() => setFilterOpen(false)}
  aria-labelledby="library-report-filters"
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",

      width: {
        xs: "94%",
        sm: 680,
        md: 760,
      },

      maxWidth: "calc(100vw - 24px)",
      maxHeight: "90vh",

      bgcolor: "background.paper",

      borderRadius: 3,

      border: "1px solid",
      borderColor: "divider",

      boxShadow: theme.shadows[20],

      overflow: "hidden",
      outline: "none",
    }}
  >
    {/* FILTER HEADER */}
    <Box
      sx={{
        px: {
          xs: 2,
          sm: 2.5,
        },
        py: 1.75,

        bgcolor: "primary.main",
        color: "#fff",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 3,
        flexShrink: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,

            bgcolor: "rgba(255,255,255,0.14)",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FilterListIcon />
        </Box>

        <Box>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              fontSize: "1.05rem",
            }}
          >
            Report Filters
          </Typography>

          <Typography
            variant="caption"
            sx={{
              opacity: 0.9,
            }}
          >
            {reportConfig.label}
          </Typography>
        </Box>
      </Stack>

      <IconButton
        onClick={() => setFilterOpen(false)}
        sx={{
          color: "#fff",
          "&:hover": {
            bgcolor: "rgba(255,255,255,0.12)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* FILTER BODY */}
    <Box
      sx={{
        p: {
          xs: 2,
          sm: 3,
        },

        maxHeight: "calc(90vh - 150px)",
        overflowY: "auto",
      }}
    >
      {/* INFO BOX */}
      <Box
        sx={{
          mb: 2.5,
          p: 1.5,

          borderRadius: 2,

          bgcolor: "action.hover",

          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="body2"
          color="text.disabled"
        >
          Use the options below to narrow down
          the report results.
        </Typography>
      </Box>

      {/* FILTER FIELDS */}
      <Box
        sx={{
          width: "100%",
        }}
      >
        {renderFilters()}
      </Box>

      {/* FILTER ACTIONS */}
      <Stack
        direction={{
          xs: "column-reverse",
          sm: "row",
        }}
        spacing={1}
        justifyContent="flex-end"
        sx={{
          mt: 3,
          pt: 2,

          borderTop: "1px solid",
          borderColor: "divider",
          position: "sticky",
          bottom: 0,
          zIndex: 3,
          backgroundColor: "background.paper",
          pb: 1.5,
        }}
      >
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => {
            const nextFilters = {
              ...getInitialFilters(user),

              organization:
                globalOrganizationId ||
                user?.organization_id ||
                user?.organization?.id ||
                user?.organization ||
                "",
            };

            setFilters(nextFilters);
            setPage(0);
          }}
        >
          Reset Filters
        </Button>

        <Button
          variant="contained"
          onClick={() => setFilterOpen(false)}
         data-app-cancel="true">
          Cancel
        </Button>

        <Button
          variant="contained"
          startIcon={
            loading ? (
              <CircularProgress
                size={17}
                color="inherit"
              />
            ) : (
              <SearchIcon />
            )
          }
          disabled={loading}
          onClick={() => fetchReport(filters)}
        >
          Apply Filters
        </Button>
      </Stack>
    </Box>
  </Box>
</Modal>

      {/* =====================================================
          PDF COLOR PICKER MODAL
      ===================================================== */}
{/* =====================================================
    PDF COLOR PICKER MODAL
===================================================== */}

<ReportPdfColorModal
  open={showColorPickerModal}
  onClose={() => setShowColorPickerModal(false)}
  themeColor={tempPdfThemeColor ||
                DEFAULT_PDF_COLORS.primary}
  setThemeColor={(hex) => { setTempPdfThemeColor(hex) }}
  textColor={tempPdfTextColor ||
                DEFAULT_PDF_COLORS.bodyText}
  setTextColor={(hex) => { setTempPdfTextColor(hex) }}
  reportTitle="Library Report"
  onGenerate={async () => {
            const generated = await generatePDF(
              tempPdfThemeColor ||
                DEFAULT_PDF_COLORS.primary,
              tempPdfTextColor ||
                DEFAULT_PDF_COLORS.bodyText
            );

            if (generated) {
              setShowColorPickerModal(false);
            }
          }}
  loading={pdfLoading}
  disabled={pdfLoading}
/>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setSnackbar(
            (prev) => ({
              ...prev,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          onClose={() =>
            setSnackbar(
              (prev) => ({
                ...prev,
                open: false,
              })
            )
          }
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {
            snackbar.message
          }
        </Alert>
      </Snackbar>
    </Box>
  );
}